import { type PointerEvent as ReactPointerEvent, useCallback } from "react";
import { useSetAtom } from "jotai";
import { emit as busEmit } from "../lib/viewerBus";
import { activeSeriesDragIdAtom, seriesDragHoverSlotAtom } from "../atoms/seriesDragAtoms";

/** Pixels the pointer must travel before a press becomes a drag (so plain
 *  clicks still select the series). */
const DRAG_THRESHOLD_PX = 6;
/** Viewport slots tag their drop target with this attribute (value = slotId). */
const DROP_SLOT_ATTR = "data-series-drop-slot";

function slotUnderPoint(x: number, y: number): string | null {
  const el = document.elementFromPoint(x, y) as Element | null;
  return el?.closest(`[${DROP_SLOT_ATTR}]`)?.getAttribute(DROP_SLOT_ATTR) ?? null;
}

/**
 * Pointer-based drag for sidebar series thumbnails → viewport slots. Replaces
 * native HTML5 drag-and-drop, which Safari/WebKit refuses to initiate on a
 * <div>; pointer events behave identically across browsers.
 *
 * Returns `startDrag`, to call from a thumbnail's `onPointerDown`. A drag only
 * begins once the pointer moves past a small threshold (so a plain click still
 * selects the series), text selection is suppressed for the press, and a clone
 * of the thumbnail follows the cursor as a visible drag image. Position updates
 * are batched into one requestAnimationFrame per frame (and the clone's
 * inherited CSS transition is disabled) so the ghost tracks the cursor smoothly
 * rather than lagging. On release over a viewport slot it emits
 * `seriesPointerDrop`, which the matching slot's ProtocolViewport turns into a
 * series assignment; the trailing click is swallowed so a drag doesn't also
 * fire select-on-click.
 */
export function useSeriesPointerDrag() {
  const setActiveDrag = useSetAtom(activeSeriesDragIdAtom);
  const setHoverSlot = useSetAtom(seriesDragHoverSlotAtom);

  const startDrag = useCallback(
    (e: ReactPointerEvent, seriesId: string) => {
      if (e.button !== 0) return; // primary button only
      const sourceEl = e.currentTarget as HTMLElement;
      const rect = sourceEl.getBoundingClientRect();
      const startX = e.clientX;
      const startY = e.clientY;
      // Where inside the thumbnail the pointer grabbed, so the clone tracks the
      // cursor naturally instead of jumping its top-left corner to the pointer.
      const offsetX = e.clientX - rect.left;
      const offsetY = e.clientY - rect.top;
      let dragging = false;
      let ghost: HTMLElement | null = null;
      let lastX = startX;
      let lastY = startY;
      let rafId = 0;

      // Suppress text selection for the whole press (restored on release) so the
      // drag never highlights underlying text.
      document.body.style.setProperty("user-select", "none");
      document.body.style.setProperty("-webkit-user-select", "none");

      // One DOM/hit-test update per animation frame, using the latest pointer
      // position — decouples the high-frequency pointermove stream from layout
      // work so the ghost moves smoothly.
      const renderFrame = () => {
        rafId = 0;
        if (ghost) {
          ghost.style.transform = `translate(${lastX - offsetX}px, ${lastY - offsetY}px)`;
        }
        setHoverSlot(slotUnderPoint(lastX, lastY));
      };
      const scheduleFrame = () => {
        if (rafId === 0) rafId = requestAnimationFrame(renderFrame);
      };

      const onMove = (ev: PointerEvent) => {
        lastX = ev.clientX;
        lastY = ev.clientY;
        if (!dragging) {
          if (Math.hypot(ev.clientX - startX, ev.clientY - startY) < DRAG_THRESHOLD_PX) return;
          dragging = true;
          window.getSelection()?.removeAllRanges();
          setActiveDrag(seriesId);
          document.body.style.cursor = "grabbing";
          // Clone the thumbnail itself as the drag image so the "box" visibly
          // moves with the cursor. cloneNode copies the preview image + label
          // (and classes); React handlers aren't copied, pointer-events:none
          // keeps it inert + out of elementFromPoint, and transition:none stops
          // the inherited .sidebar-thumbnail transition from lagging the move.
          ghost = sourceEl.cloneNode(true) as HTMLElement;
          ghost.style.cssText =
            `position:fixed;left:0;top:0;margin:0;z-index:9999;pointer-events:none;` +
            `width:${rect.width}px;opacity:0.9;box-shadow:0 6px 18px rgba(0,0,0,0.55);` +
            `transition:none;will-change:transform;` +
            `transform:translate(${startX - offsetX}px, ${startY - offsetY}px);`;
          document.body.appendChild(ghost);
        }
        scheduleFrame();
      };

      const finish = (ev: PointerEvent) => {
        window.removeEventListener("pointermove", onMove);
        window.removeEventListener("pointerup", finish);
        window.removeEventListener("pointercancel", finish);
        if (rafId !== 0) cancelAnimationFrame(rafId);
        document.body.style.removeProperty("user-select");
        document.body.style.removeProperty("-webkit-user-select");
        if (!dragging) return;
        ghost?.remove();
        ghost = null;
        document.body.style.cursor = "";
        setActiveDrag(null);
        setHoverSlot(null);
        if (ev.type === "pointerup") {
          const slotId = slotUnderPoint(ev.clientX, ev.clientY);
          if (slotId) busEmit("seriesPointerDrop", { seriesId, slotId });
          // Swallow the click the browser fires after a drag so it doesn't also
          // trigger the thumbnail's select-on-click. One-shot, capture phase.
          const swallow = (ce: MouseEvent) => {
            ce.stopPropagation();
            ce.preventDefault();
            window.removeEventListener("click", swallow, true);
          };
          window.addEventListener("click", swallow, true);
          window.setTimeout(() => window.removeEventListener("click", swallow, true), 100);
        }
      };

      window.addEventListener("pointermove", onMove);
      window.addEventListener("pointerup", finish);
      window.addEventListener("pointercancel", finish);
    },
    [setActiveDrag, setHoverSlot],
  );

  return { startDrag };
}
